import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { PodDto, w2d2dApiClient } from "../../w2d2d-api-client.ts";

export interface PodsControl {
    readonly pods: PodDto[] | undefined;
    readonly activePods: PodDto[] | undefined;
    readonly addPod: (name: string) => Promise<void>;
    readonly enablePod: (name: string) => Promise<void>;
    readonly disablePod: (name: string) => Promise<void>;
    readonly deletePod: (name: string) => Promise<void>;
}

const PodsControlContext = createContext<PodsControl | undefined>(undefined);

interface PodsControlProviderProps {
    children: ReactNode | ReactNode[];
}

export const PodsControlProvider = (props: PodsControlProviderProps) => {
    const podsControl = usePodsControlImpl();

    return (
        <PodsControlContext.Provider value={podsControl}>
            {props.children}
        </PodsControlContext.Provider>
    );
};

export function usePodsControl(): PodsControl {
    const context = useContext(PodsControlContext);
    if (context === undefined) {
        throw new Error('usePodsControl must be used within PodsControlProvider');
    }
    return context;
}

function usePodsControlImpl(): PodsControl {
    const [pods, setPods] = useState<PodDto[] | undefined>(undefined)

    const fetchPods = useCallback(async () => {
        const result = await w2d2dApiClient.getPods()
        setPods(result.pods.sort((a, b) => a.name.localeCompare(b.name)))
    }, [])

    useEffect(() => {
        let active = true
        w2d2dApiClient.getPods()
            .then((result) => {
                if (active) {
                    setPods(result.pods.sort((a, b) => a.name.localeCompare(b.name)))
                }
            })
        return () => {
            active = false
        }
    }, [])

    const activePods = useMemo(() => {
        return pods?.filter(p => p.enabled)
    }, [pods])

    const addPod = useCallback(async (name: string) => {
        await w2d2dApiClient.addPod({ name })
        await fetchPods()
    }, [fetchPods])

    const enablePod = useCallback(async (name: string) => {
        await w2d2dApiClient.enablePod({ name })
        await fetchPods()
    }, [fetchPods])

    const disablePod = useCallback(async (name: string) => {
        await w2d2dApiClient.disablePod({ name })
        await fetchPods()
    }, [fetchPods])

    const deletePod = useCallback(async (name: string) => {
        await w2d2dApiClient.deletePod({ name })
        await fetchPods()
    }, [fetchPods])

    return {
        pods,
        activePods,
        addPod,
        enablePod,
        disablePod,
        deletePod,
    }
}
